import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Award,
  Share2,
  CheckCircle,
  Trophy,
  Leaf,
  Copy,
  ExternalLink,
  Sparkles,
  Check,
  Globe,
  ShieldCheck,
  X,
  Truck,
  Navigation,
  Crown,
  Flame,
  CheckSquare
} from 'lucide-react';
import {
  FaWhatsapp,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaInstagram
} from 'react-icons/fa';

export const MilestonesAndShare = ({
  userName = "User",
  totalVehicles = 0,
  totalEmission = 0,
  isKycVerified = false,
  fleetList = [],
}) => {
  const [copied, setCopied] = useState(false);
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [activeTab, setActiveTab] = useState('milestones'); // 'milestones' | 'share'

  // Exact Actual Distance Driven Across Fleet
  const totalDistanceDriven = fleetList.reduce((sum, item) => {
    const dist = parseFloat(item.totalDistance || item.distance || 0);
    return sum + (isNaN(dist) ? 0 : dist);
  }, 0);

  // EPA Standard Tree Offset Calculation
  const treesEquivalent = totalEmission > 0 ? (totalEmission / 21.7).toFixed(1) : '0.0';

  // EXACTLY 6 CORE MILESTONES (As Requested)
  const coreMilestones = [
    {
      id: 'm1_kyc',
      title: 'KYC Verified Member',
      category: 'Identity Security',
      badgeLabel: 'Verified Member 🛡️',
      targetText: 'Complete 1-time KYC verification',
      currentValueText: isKycVerified ? 'Verified ✅' : 'Pending Verification ⏳',
      progress: isKycVerified ? 100 : 0,
      isUnlocked: isKycVerified,
      icon: ShieldCheck,
    },
    {
      id: 'm2_fleet_50',
      title: 'Mega Fleet Leader (50+ Vehicles)',
      category: 'Fleet Scale',
      badgeLabel: 'Enterprise 🚛',
      targetText: 'Register 50 vehicles in your active fleet',
      currentValueText: `${totalVehicles} / 50 Vehicles`,
      progress: Math.min(100, Math.round((totalVehicles / 50) * 100)),
      isUnlocked: totalVehicles >= 50,
      icon: Truck,
    },
    {
      id: 'm3_fleet_100',
      title: 'Centurion Fleet Enterprise (100+ Vehicles)',
      category: 'Fleet Scale',
      badgeLabel: 'Centurion 👑',
      targetText: 'Register 100 vehicles in your active fleet',
      currentValueText: `${totalVehicles} / 100 Vehicles`,
      progress: Math.min(100, Math.round((totalVehicles / 100) * 100)),
      isUnlocked: totalVehicles >= 100,
      icon: Crown,
    },
    {
      id: 'm4_km_10k',
      title: '10,000 KM Transit Master',
      category: 'Transit Distance',
      badgeLabel: '10,000 KM 🌍',
      targetText: 'Log 10,000 km total fleet distance',
      currentValueText: `${totalDistanceDriven.toFixed(0)} / 10,000 km`,
      progress: Math.min(100, Math.round((totalDistanceDriven / 10000) * 100)),
      isUnlocked: totalDistanceDriven >= 10000,
      icon: Globe,
    },
    {
      id: 'm5_km_100k',
      title: '100,000 KM Century Express',
      category: 'Transit Distance',
      badgeLabel: '100K Express 🚀',
      targetText: 'Log 100,000 km total fleet distance',
      currentValueText: `${totalDistanceDriven.toFixed(0)} / 100,000 km`,
      progress: Math.min(100, Math.round((totalDistanceDriven / 100000) * 100)),
      isUnlocked: totalDistanceDriven >= 100000,
      icon: Navigation,
    },
    {
      id: 'm6_co2_1000',
      title: '1,000 kg CO₂ Carbon Analyst',
      category: 'Carbon Footprint',
      badgeLabel: '1 Tonne Club ⚡',
      targetText: 'Track 1,000 kg of CO₂ emissions',
      currentValueText: `${totalEmission.toFixed(1)} / 1,000 kg CO₂`,
      progress: Math.min(100, Math.round((totalEmission / 1000) * 100)),
      isUnlocked: totalEmission >= 1000,
      icon: Flame,
    },
  ];

  const unlockedCount = coreMilestones.filter((m) => m.isUnlocked).length;
  const overallProgress = Math.round((unlockedCount / coreMilestones.length) * 100);

  // Dynamic Share Message
  const siteUrl = window.location.origin;
  const shareMessage = `🏆 Green Fleet Milestones Report on Pure Prakruti 🏆\n\n👤 User: ${userName}\n🚛 Registered Fleet: ${totalVehicles} Vehicle(s)\n📍 Total Transit Distance: ${totalDistanceDriven.toFixed(1)} km\n💨 CO₂ Monitored: ${totalEmission.toFixed(2)} kg\n🌳 Tree Offset Equivalent: ~${treesEquivalent} Trees\n🏆 Milestones Achieved: ${unlockedCount} of 6 Unlocked (${overallProgress}%)\n\nJoin the green logistics revolution with Pure Prakruti! 🌿`;

  const encodedMessage = encodeURIComponent(shareMessage);
  const encodedUrl = encodeURIComponent(siteUrl);

  const shareLinks = [
    {
      name: 'WhatsApp',
      icon: FaWhatsapp,
      bg: 'bg-emerald-600 hover:bg-emerald-700',
      textColor: 'text-white',
      url: `https://api.whatsapp.com/send?text=${encodedMessage}%20${encodedUrl}`,
      type: 'link',
    },
    {
      name: 'Instagram',
      icon: FaInstagram,
      bg: 'bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 hover:opacity-90',
      textColor: 'text-white',
      type: 'instagram',
    },
    {
      name: 'LinkedIn',
      icon: FaLinkedin,
      bg: 'bg-blue-700 hover:bg-blue-800',
      textColor: 'text-white',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      type: 'link',
    },
    {
      name: 'Twitter / X',
      icon: FaTwitter,
      bg: 'bg-sky-500 hover:bg-sky-600',
      textColor: 'text-white',
      url: `https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedUrl}`,
      type: 'link',
    },
    {
      name: 'Facebook',
      icon: FaFacebook,
      bg: 'bg-blue-600 hover:bg-blue-700',
      textColor: 'text-white',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      type: 'link',
    },
  ];

  const handleCopyLink = () => {
    const textToCopy = `${shareMessage}\n\nCheck out Pure Prakruti: ${siteUrl}`;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleInstagramClick = async () => {
    const textToCopy = `${shareMessage}\n\nCheck out Pure Prakruti: ${siteUrl}`;
    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
      console.log('Clipboard write failed', err);
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Pure Prakruti Core Fleet Milestones',
          text: shareMessage,
          url: siteUrl,
        });
        return;
      } catch (err) {
        console.log('Native share dismissed', err);
      }
    }

    setShowInstagramModal(true);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Pure Prakruti Core Fleet Milestones',
          text: shareMessage,
          url: siteUrl,
        });
      } catch (err) {
        console.log('Share dismissed', err);
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <motion.div
      className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm mb-8 relative overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header & Tab Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#0E3B2E] text-amber-300 flex items-center justify-center font-bold shadow-md border border-emerald-700/50">
            <Trophy className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              Fleet Key Milestones
              <Sparkles className="w-4 h-4 text-amber-500 animate-pulse" />
            </h3>
            <p className="text-xs text-gray-500">
              6 core milestones calculated live from your fleet records.
            </p>
          </div>
        </div>

        {/* Tab Buttons */}
        <div className="flex bg-gray-100 p-1 rounded-xl shrink-0 self-start sm:self-auto">
          <button
            onClick={() => setActiveTab('milestones')}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
              activeTab === 'milestones'
                ? 'bg-[#0E3B2E] text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Trophy className="w-3.5 h-3.5" />
            6 Key Milestones ({unlockedCount}/6)
          </button>
          <button
            onClick={() => setActiveTab('share')}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
              activeTab === 'share'
                ? 'bg-[#0E3B2E] text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Share2 className="w-3.5 h-3.5" />
            Share Stats
          </button>
        </div>
      </div>

      {/* TAB CONTENT 1: EXACT 6 CORE MILESTONES */}
      {activeTab === 'milestones' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          {/* Real Data Hero Banner */}
          <div className="bg-gradient-to-br from-[#0E3B2E] via-[#14532d] to-[#064e3b] text-white p-6 md:p-8 rounded-2xl mb-8 shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 opacity-10 translate-x-6 -translate-y-6">
              <Trophy size={240} />
            </div>

            <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Stat 1 */}
              <div className="bg-white/10 backdrop-blur-md border border-white/15 p-4 rounded-2xl space-y-1">
                <div className="text-xs text-emerald-200 font-medium flex items-center gap-1.5">
                  <Truck className="w-4 h-4 text-emerald-400" />
                  <span>Fleet Size</span>
                </div>
                <div className="text-2xl font-extrabold text-white">{totalVehicles} Vehicles</div>
                <div className="text-[11px] text-emerald-100/80">Target: 50 & 100 Vehicles</div>
              </div>

              {/* Stat 2 */}
              <div className="bg-white/10 backdrop-blur-md border border-white/15 p-4 rounded-2xl space-y-1">
                <div className="text-xs text-emerald-200 font-medium flex items-center gap-1.5">
                  <Navigation className="w-4 h-4 text-sky-400" />
                  <span>Fleet Transit Distance</span>
                </div>
                <div className="text-2xl font-extrabold text-white">{totalDistanceDriven.toFixed(0)} km</div>
                <div className="text-[11px] text-emerald-100/80">Target: 10k & 100k km</div>
              </div>

              {/* Stat 3 */}
              <div className="bg-white/10 backdrop-blur-md border border-white/15 p-4 rounded-2xl space-y-1">
                <div className="text-xs text-emerald-200 font-medium flex items-center gap-1.5">
                  <Leaf className="w-4 h-4 text-amber-300" />
                  <span>CO₂ Emission Monitored</span>
                </div>
                <div className="text-2xl font-extrabold text-white">{totalEmission.toFixed(1)} kg</div>
                <div className="text-[11px] text-emerald-100/80">Target: 1,000 kg CO₂</div>
              </div>

              {/* Stat 4 */}
              <div className="bg-white/10 backdrop-blur-md border border-white/15 p-4 rounded-2xl space-y-1">
                <div className="text-xs text-emerald-200 font-medium flex items-center gap-1.5">
                  <Crown className="w-4 h-4 text-amber-400" />
                  <span>Milestones Achieved</span>
                </div>
                <div className="text-2xl font-extrabold text-amber-300">{unlockedCount} of 6 Unlocked</div>
                <div className="text-[11px] text-emerald-100/80">{overallProgress}% Complete</div>
              </div>
            </div>

            {/* Overall Progress Line */}
            <div className="mt-6 pt-4 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-emerald-100 font-medium">
                Core Milestones Progress: <strong>{unlockedCount} of 6 Badges Unlocked ({overallProgress}%)</strong>
              </div>
              <div className="w-full sm:w-64 bg-black/30 rounded-full h-3 overflow-hidden p-0.5 border border-white/20">
                <div
                  className="bg-gradient-to-r from-amber-400 to-emerald-300 h-full rounded-full transition-all duration-700"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Grid of EXACTLY 6 Core Milestones */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {coreMilestones.map((m) => {
              const IconComp = m.icon;
              return (
                <div
                  key={m.id}
                  className={`p-6 rounded-2xl border transition-all flex flex-col justify-between relative ${
                    m.isUnlocked
                      ? 'bg-emerald-50/70 border-emerald-300 shadow-sm hover:shadow-md'
                      : 'bg-white border-gray-200 shadow-sm hover:border-gray-300'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                          m.isUnlocked
                            ? 'bg-[#0E3B2E] text-amber-300 shadow-sm'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        <IconComp className="w-5 h-5" />
                      </div>
                      <span
                        className={`text-[11px] font-bold px-3 py-1 rounded-full border ${
                          m.isUnlocked
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                            : 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}
                      >
                        {m.badgeLabel}
                      </span>
                    </div>

                    <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-800 mb-1">
                      {m.category}
                    </div>

                    <h4 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                      {m.title}
                      {m.isUnlocked && <CheckCircle className="w-4 h-4 text-emerald-600 inline" />}
                    </h4>

                    <p className="text-xs text-gray-500 mb-4 leading-relaxed">{m.targetText}</p>
                  </div>

                  <div>
                    <div className="flex justify-between text-[11px] font-bold text-gray-800 mb-1.5">
                      <span>Status:</span>
                      <span className={m.isUnlocked ? 'text-emerald-700' : 'text-gray-700'}>
                        {m.currentValueText}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          m.isUnlocked ? 'bg-emerald-600' : 'bg-amber-500'
                        }`}
                        style={{ width: `${m.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* TAB CONTENT 2: SOCIAL MEDIA SHARE */}
      {activeTab === 'share' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="space-y-6">
          {/* Share Preview Card */}
          <div className="bg-emerald-950 text-white rounded-2xl p-6 border border-emerald-700/40 relative overflow-hidden shadow-lg">
            <div className="absolute -right-8 -bottom-8 opacity-10">
              <Trophy size={180} />
            </div>

            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-400 bg-emerald-900/60 px-3 py-1 rounded-full border border-emerald-700/50 flex items-center gap-1">
                <CheckSquare className="w-3.5 h-3.5 text-amber-400" /> Shareable Milestones Card
              </span>
              <span className="text-xs text-emerald-300 font-medium">Pure Prakruti System</span>
            </div>

            <div className="space-y-2 mb-4">
              <h4 className="text-xl font-extrabold text-white flex items-center gap-2">
                🏆 {userName}'s Key Fleet Milestones Report
              </h4>
              <p className="text-sm text-emerald-100/90 whitespace-pre-line leading-relaxed bg-white/5 p-4 rounded-xl border border-white/10 font-mono text-xs">
                {shareMessage}
              </p>
            </div>

            <div className="flex items-center gap-4 text-xs text-emerald-300 flex-wrap">
              <span className="flex items-center gap-1">
                <Truck className="w-4 h-4 text-emerald-400" /> {totalVehicles} Vehicles
              </span>
              <span className="flex items-center gap-1">
                <Navigation className="w-4 h-4 text-sky-400" /> {totalDistanceDriven.toFixed(0)} km
              </span>
              <span className="flex items-center gap-1">
                <Trophy className="w-4 h-4 text-amber-400" /> {unlockedCount} of 6 Milestones Unlocked
              </span>
            </div>
          </div>

          {/* Social Media Buttons */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-3">Share your milestones report to social media:</h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {shareLinks.map((item) => {
                const Icon = item.icon;
                if (item.type === 'instagram') {
                  return (
                    <button
                      key={item.name}
                      onClick={handleInstagramClick}
                      className={`flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl font-bold text-xs ${item.bg} ${item.textColor} shadow-sm transition transform hover:-translate-y-0.5 cursor-pointer`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                      <ExternalLink className="w-3 h-3 opacity-70" />
                    </button>
                  );
                }

                return (
                  <a
                    key={item.name}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl font-bold text-xs ${item.bg} ${item.textColor} shadow-sm transition transform hover:-translate-y-0.5 cursor-pointer`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                    <ExternalLink className="w-3 h-3 opacity-70" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Native Share & Copy Link */}
          <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
            <button
              onClick={handleCopyLink}
              className="w-full sm:w-auto flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs px-5 py-3 rounded-xl transition border border-gray-300 cursor-pointer"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
              <span>{copied ? 'Copied to Clipboard!' : 'Copy Achievement Text & Link'}</span>
            </button>

            {'share' in navigator && (
              <button
                onClick={handleNativeShare}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold text-xs px-6 py-3 rounded-xl transition shadow-md cursor-pointer"
              >
                <Share2 className="w-4 h-4 text-emerald-400" />
                <span>More Share Options</span>
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Instagram Guide Modal */}
      {showInstagramModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative border border-purple-200">
            <button
              onClick={() => setShowInstagramModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-purple-600 via-pink-600 to-amber-500 text-white flex items-center justify-center mb-4 shadow-md">
              <FaInstagram className="w-6 h-6" />
            </div>

            <h3 className="text-xl font-extrabold text-gray-900 mb-2">
              Share to Instagram
            </h3>

            <p className="text-xs text-gray-600 mb-4 leading-relaxed">
              Instagram requires manual text pasting for web posts & stories. We have automatically copied your core fleet report text to your clipboard!
            </p>

            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4 space-y-3 mb-6 text-xs text-purple-900">
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center shrink-0 text-[10px]">1</span>
                <span><strong>Report Copied!</strong> Your milestone statistics are saved on your clipboard.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center shrink-0 text-[10px]">2</span>
                <span>Click below to open <strong>Instagram</strong>.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center shrink-0 text-[10px]">3</span>
                <span>Press <strong>Paste (Ctrl+V / Long Press)</strong> when sharing your Post or Story!</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  window.open('https://www.instagram.com/', '_blank');
                  setShowInstagramModal(false);
                }}
                className="flex-1 bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500 text-white font-bold text-xs py-3 rounded-xl shadow-md hover:opacity-95 transition text-center cursor-pointer"
              >
                Open Instagram App / Web 🚀
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default MilestonesAndShare;
